/**
 * Правка одной клетки матрицы «поле × год» (AK-772 PR-2).
 *
 * Форма НЕ своя: внутри диалога стоит штатный `RotationYearEditor` карточки
 * поля — тот самый, где уже живут выпадашка культур (алфавит + поиск), виды
 * пара, «одновременно / одна за другой» и многолетники. Второй редактор года
 * означал бы, что половина этих правил действует через раз, в зависимости от
 * того, с какого экрана зашли.
 *
 * Что диалог добавляет своего — ровно две вещи из решения founder 31.08:
 * подпись «откуда взялась запись» и «История изменений клетки» (журнал
 * `field_history_changes`, кнопкой, а не полотном).
 */
import { useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import { formatRuDateNumeric } from '@/lib/dateFmt';
import { RotationYearEditor } from '@/components/fields/card/history/RotationYearEditor';
import { useRotation, usePutRotationYear } from '@/components/fields/card/history/api';
import type { RotationYearBody } from '@/components/fields/card/history/api';
import { useCellLog, type HistoryCell } from './api';

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e as ApiError & { detail?: unknown }).detail;
    if (d && typeof d === 'object' && 'message' in d) return String((d as { message: unknown }).message);
    return e.message;
  }
  return e instanceof Error ? e.message : 'Не удалось сохранить год.';
}

export interface CellTarget {
  farmId: string;
  fieldId: string;
  fieldName: string;
  year: number;
  cell: HistoryCell | undefined;
}

export function CellDialog({
  target,
  onClose,
  onSaved,
}: {
  target: CellTarget | null;
  onClose: () => void;
  /** Сетка и счётчики шага пересчитываются после КАЖДОЙ правки. */
  onSaved: () => void;
}) {
  const [showLog, setShowLog] = useState(false);
  const fieldId = target?.fieldId ?? '';
  const rotation = useRotation(fieldId);
  const save = usePutRotationYear(fieldId);
  const log = useCellLog(target?.farmId ?? null, showLog ? fieldId : null, target?.year ?? null);

  if (!target) return null;
  const year = rotation.data?.years.find((y) => y.year === target.year);

  const onSave = (body: RotationYearBody) => {
    save.mutate(
      { year: target.year, body },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          setShowLog(false);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {target.fieldName} · {target.year} год
          </DialogTitle>
          <DialogDescription>
            {target.cell
              ? 'Год заполнен — правка перепишет его и попадёт в журнал клетки.'
              : 'Год пустой: без него на этом поле не работает правило севооборота.'}
          </DialogDescription>
        </DialogHeader>

        {/* «Откуда взялась запись» — прямо в форме, серым (макет v3). */}
        {target.cell && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {target.cell.manual ? (
              <Badge variant="outline">изменено вручную — импорт это не перезапишет</Badge>
            ) : target.cell.from_import ? (
              <Badge variant="outline">импорт «История поля»</Badge>
            ) : null}
            {target.cell.edited_by && (
              <span>
                ввёл {target.cell.edited_by}
                {target.cell.edited_at ? ` ${formatRuDateNumeric(target.cell.edited_at)}` : ''}
              </span>
            )}
          </div>
        )}

        {rotation.isLoading && <Skeleton className="h-40 w-full" />}
        {rotation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              Не получилось прочитать историю поля. Обновите страницу — если не поможет, напишите
              администратору.
            </AlertDescription>
          </Alert>
        )}

        {rotation.data && (
          <RotationYearEditor
            year={target.year}
            fieldAreaHa={rotation.data.field_area_ha}
            isRiceSystem={false}
            existing={year}
            // «Следующий пустой» внутри диалога не предлагаем: перескок между
            // годами — работа матрицы, там видно всю строку сразу.
            nextEmpty={null}
            saving={save.isPending}
            error={save.error ? errText(save.error) : null}
            onCancel={onClose}
            onSave={onSave}
          />
        )}

        <div className="border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowLog((v) => !v)}
            data-testid="toggle-cell-log"
          >
            <History className="mr-2 h-4 w-4" />
            История изменений клетки
          </Button>
          {showLog && (
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              {log.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {log.data?.items.length === 0 && <div>Эту клетку ещё никто не правил.</div>}
              {log.data?.items.map((it, i) => (
                <div key={`${it.changed_at}-${i}`} data-testid="cell-log-entry">
                  {it.change_kind === 'overwrite'
                    ? `было «${it.old_label ?? '—'}» → стало «${it.new_label ?? '—'}»`
                    : `заполнено «${it.new_label ?? '—'}»`}
                  {it.changed_by ? `, изменил ${it.changed_by}` : ''}
                  {`, ${formatRuDateNumeric(it.changed_at)}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
